import React, { useState } from 'react';
import { Search, SlidersHorizontal, ShieldCheck, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RecommendationFilterState } from '@/types/recommendation';
import { RecommendationFilterPopover } from './RecommendationFilterPopover';

interface RecommendationSearchBarProps {
  filter: RecommendationFilterState;
  onFilterChange: (filter: RecommendationFilterState) => void;
  availableRoles: string[];
  availableLocations: string[];
  availableDepartments: string[];
  /** The role auto-applied from the user's context (role-based smart filter) */
  defaultRoleFilter?: string | null;
  /** Called when the user explicitly clears the role-based default filter */
  onClearDefaultRole?: () => void;
}

function countActiveFilters(filter: RecommendationFilterState, defaultRole?: string | null): number {
  let count = 0;
  // Don't count the auto-applied role filter — it's shown separately
  if (filter.roleFilter && filter.roleFilter !== defaultRole) count++;
  if (filter.locationFilter) count++;
  if (filter.departmentFilter) count++;
  if (filter.minMatchScore > 0) count++;
  return count;
}

export function RecommendationSearchBar({
  filter,
  onFilterChange,
  availableRoles,
  availableLocations,
  availableDepartments,
  defaultRoleFilter,
  onClearDefaultRole,
}: RecommendationSearchBarProps) {
  const [filterOpen, setFilterOpen] = useState(false);

  // The role filter is "default/auto" when it matches the defaultRoleFilter prop
  const isUsingDefaultRole =
    !!defaultRoleFilter && filter.roleFilter === defaultRoleFilter;

  // Count only manually applied filters
  const activeCount = countActiveFilters(filter, defaultRoleFilter);

  const filterButton = (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7"
        aria-label="Open filters"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
      </Button>
      {activeCount > 0 && (
        <Badge
          variant="secondary"
          className="absolute -top-1.5 -right-1.5 h-4 w-4 p-0 flex items-center justify-center text-[10px] rounded-full"
        >
          {activeCount}
        </Badge>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {/* Role-based smart filter pill — shown when the default role filter is active */}
      {isUsingDefaultRole && (
        <div className="flex items-center gap-1.5 accent-teal">
          <Badge variant="accent" accent="teal" className="gap-1 text-xs pl-1.5 pr-1 py-0.5 h-6">
            <ShieldCheck className="h-3 w-3 shrink-0" />
            <span className="truncate">
              Showing <span className="font-semibold">{defaultRoleFilter}</span> matches
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    onFilterChange({ ...filter, roleFilter: null });
                    onClearDefaultRole?.();
                  }}
                  className="ml-0.5 h-4 w-4 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors shrink-0"
                  aria-label={`Remove ${defaultRoleFilter} role filter and show all roles`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Show all roles</TooltipContent>
            </Tooltip>
          </Badge>
        </div>
      )}

      {/* Manual override notice */}
      {defaultRoleFilter && !isUsingDefaultRole && filter.roleFilter === null && (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="gap-1 text-xs pl-1.5 pr-2 py-0.5 h-6 text-muted-foreground">
            <ShieldCheck className="h-3 w-3 shrink-0" />
            Showing all roles
          </Badge>
          <Separator orientation="vertical" className="h-4" />
          <button
            type="button"
            onClick={() => onFilterChange({ ...filter, roleFilter: defaultRoleFilter })}
            className="text-xs text-primary font-medium hover:underline underline-offset-2 shrink-0"
            aria-label={`Restore ${defaultRoleFilter} role filter`}
          >
            Restore default
          </button>
        </div>
      )}

      {/* Search + filter row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            className="h-7 text-xs pl-6 pr-2"
            placeholder="Search name, role, skill…"
            value={filter.searchQuery}
            onChange={e => onFilterChange({ ...filter, searchQuery: e.target.value })}
          />
        </div>
        <RecommendationFilterPopover
          open={filterOpen}
          onOpenChange={setFilterOpen}
          filter={filter}
          onFilterChange={onFilterChange}
          availableRoles={availableRoles}
          availableLocations={availableLocations}
          availableDepartments={availableDepartments}
          trigger={filterButton}
        />
      </div>
    </div>
  );
}
